// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Turns an alarm into a work order draft, for raising external work straight
 * from the alarm an operator is already looking at.
 *
 * Pure — no I/O — so the mapping is testable without rendering anything. The
 * operator sees and can edit every value before it is sent; nothing here is
 * submitted on their behalf.
 */

import type { Alarm, Severity } from '../types/alarm';
import type { WorkOrderCreateRequest, WorkOrderPriority } from '../types';

/**
 * Alarm severity carries across as work order priority. The provider system's
 * scale has no P1–P4 equivalent, so this is a judgement, not a lookup: P1 and P2
 * are the ones someone should be dispatched for out of hours.
 */
const PRIORITY_BY_SEVERITY: Record<Severity, WorkOrderPriority> = {
  P1: 'HIGH',
  P2: 'HIGH',
  P3: 'MEDIUM',
  P4: 'LOW',
  Unknown: 'NONE',
};

function describe(alarm: Alarm): string {
  const where = [
    `Raised from Helios alarm ${alarm.ruleId ?? alarm.id}.`,
    `Asset: ${alarm.asset}${alarm.location ? ` (${alarm.location})` : ''}`,
    alarm.siteLabel || alarm.siteId ? `Site: ${alarm.siteLabel ?? alarm.siteId}` : '',
    alarm.point ? `Point: ${alarm.point}` : '',
    `Severity: ${alarm.sev}`,
    alarm.eventTimestamp ? `Raised at: ${alarm.eventTimestamp}` : '',
  ].filter(Boolean);

  const what = [
    alarm.issue ? `Issue: ${alarm.issue}` : '',
    alarm.diagnosis ? `Diagnosis: ${alarm.diagnosis}` : '',
    alarm.summary ? `Summary: ${alarm.summary}` : '',
  ].filter(Boolean);

  return [where.join('\n'), what.join('\n')].filter(Boolean).join('\n\n').trim();
}

export function workOrderDraftFromAlarm(alarm: Alarm): Partial<WorkOrderCreateRequest> {
  const subject = alarm.issue || alarm.summary || 'Investigate alarm';
  return {
    title: `${alarm.asset}: ${subject}`,
    description: describe(alarm),
    // The only correlation field the work order API offers. It is free text that
    // the external system stores and echoes back — not a validated link — so an
    // alarm reference is as legitimate here as a planned-task id.
    originating_task: alarm.ruleId ?? alarm.id,
    priority: PRIORITY_BY_SEVERITY[alarm.sev] ?? 'NONE',
    ...(alarm.alarmId && {
      source_alarm: {
        alarm_id: alarm.alarmId,
        alarm_code: alarm.id,
        site_id: alarm.siteId,
        equipment_id: alarm.assetId ?? null,
        label: alarmLinkLabel(alarm),
      },
    }),
  };
}

/** "GEN-1-A · Low fuel pressure": how a work order names the alarm it came from. */
export function alarmLinkLabel(alarm: Pick<Alarm, 'asset' | 'issue'>): string {
  return [alarm.asset, alarm.issue].filter(Boolean).join(' · ');
}
